// @flow

import React from "react";
import classNames from "classnames";

import optionResolver from "../utils/options";
import type { SchemaTableWidgetProps } from "../types";

import styles from "./seconds-as-hours-input.scss";

const SECONDS_PER_HOUR = 3600;

export type SecondsAsHoursWidgetState = $Exact<{
  data: number,
  touched: boolean,
}>;

export class SecondsAsHoursWidget extends React.Component<
  SchemaTableWidgetProps<number>,
  SecondsAsHoursWidgetState,
> {
  state = {
    data: this.props.data,
    touched: false,
  };

  componentDidUpdate(prevProps: SchemaTableWidgetProps<number>) {
    let nextState = {};

    if (prevProps.data !== this.props.data) {
      nextState = { ...nextState, data: this.props.data };
    }

    if (!this.props.touched && prevProps.touched) {
      nextState = { ...nextState, touched: false };
    }

    if (Object.keys(nextState).length) {
      this.setState(nextState);
    }
  }

  toHours = (seconds: number) =>
    isNaN(seconds) ? 0 : Math.round((seconds / SECONDS_PER_HOUR) * 10) / 10;

  toSeconds = (hours: number) => hours * SECONDS_PER_HOUR;

  handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
    this.setState({
      data: this.toSeconds(+event.currentTarget.value),
    });
  };

  submitChange = (event: SyntheticEvent<HTMLInputElement>) => {
    this.setState({ touched: true });

    this.props.onChange(
      !isNaN(event.currentTarget.value)
        ? this.toSeconds(+event.currentTarget.value)
        : this.state.data,
    );
  };

  hasUiSchemaError = (data: number): boolean => {
    if (!this.state.touched) {
      return false;
    }

    return data === null || data === undefined || typeof data !== "number";
  };

  hasError = (value: number): boolean => {
    return !!this.props.error || this.hasUiSchemaError(value);
  };

  render() {
    const { active, options } = this.props;
    const disabled = optionResolver.resolveBoolean((options || {}).disabled, this.props.row);

    if (!active) {
      return (
        <div
          className={classNames("seconds-as-hours-input__readonly", {
            text__disabled: disabled,
          })}
        >
          {this.toHours(this.state.data)}
        </div>
      );
    }

    const minimum = optionResolver.resolveNumber((options || {}).minimum, this.props.row);
    const maximum = optionResolver.resolveNumber((options || {}).maximum, this.props.row);
    const minimumInHours = typeof minimum === "number" ? minimum / SECONDS_PER_HOUR : undefined;
    const maximumInHours = typeof maximum === "number" ? maximum / SECONDS_PER_HOUR : undefined;

    return (
      <input
        type="number"
        className={classNames("form-control", {
          "seconds-as-hours-input__disabled": disabled,
          "seconds-as-hours-input__error": this.hasError(this.props.data),
        })}
        value={this.toHours(this.state.data)}
        required={this.props.required}
        min={minimumInHours}
        max={maximumInHours}
        disabled={disabled}
        onChange={this.handleChange}
        onBlur={this.submitChange}
        autoFocus="autofocus"
      />
    );
  }
}

export default SecondsAsHoursWidget;
